import { O, pipe } from '@mobily/ts-belt';
import { Alert, Box, Button, CircularProgress, Grid, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'common/hooks/useTranslation';
import { Lambda } from 'common/types';
import { FC, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { IncomingFiberSchema, incomingFiberSchema } from 'app/areas/types/IncomingFiberSchema';
import { IncomingFiberDetails } from 'app/areas/types/ObjectDetails';
import { PhotosField } from 'app/areas/components/PhotosField/PhotosField';
import { useErrorMessage } from 'app/ui/validationError/useErrorMessage';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useCreateBundle } from 'app/areas/hooks/useCreate';
import { useAreaPhotoUpload } from 'app/areas/hooks/useAreaPhotoUpload';
import { IncomingFiber } from '../ObjectDetails/IncomingFiber';
import { OptionalSelect } from '../../ui/OptionalSelect';
import { useGetPropertyConnections } from '../../hooks/useGetPropertyConnections';
import { getObject } from '../../getObject';
import { getAreas } from '../../getAreas';
import { EnlargedPhotoDialog } from '../EnlargedPhotoDialog';

export const CreateIncomingFiber: FC<{ onSave: Lambda<void, void> }> = ({ onSave }) => {
  const { status, data } = useGetPropertyConnections();
  const [objectId, setObjectId] = useState<O.Option<string>>(O.None);
  const { t } = useTranslation();

  // Get pre-generated entity ID and property ID for offline photo tracking
  const { entityId: preGeneratedId, entityType, propertyId } = useAreaPhotoUpload('bundle');

  const [pickedImage, setPickedImage] = useState<O.Option<string>>(O.None);
  const { mutate: createIncomingFiber, isLoading: isLoadingCreateIncomingFiber } = useCreateBundle({
    onSuccess: () => {
      onSave();
      toast.success(t('general.updatedSuccessfully'));
    },
    onError: () => {
      toast.error(t('error.generalMessage'));
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<IncomingFiberSchema>({
    defaultValues: {
      text: '',
      connectionsCount: 8,
      location: '',
      netOwner: '',
      room: '',
      length: 0,
    },
    mode: 'onChange',
    resolver: zodResolver(incomingFiberSchema),
  });

  const [photosURLs, setPhotosURLs] = useState<IncomingFiberDetails['photosURLs']>([]);

  const getErrorMessage = useErrorMessage(errors);

  if (status === 'loading') {
    return <CircularProgress />;
  }

  if (status === 'error') {
    return <Alert severity="error">{t('areas.properties.error')}</Alert>;
  }

  const { incomingFibers } = getAreas(data);

  const selectedObjectDetails = pipe(
    objectId,
    O.flatMap((id) => getObject(id)(data)),
  );

  const handleSave = (values: IncomingFiberSchema) => {
    // Filter blob URLs and use pre-generated ID so photos are associated with this entity
    createIncomingFiber({ ...values, id: preGeneratedId, photosURLs });
  };

  return (
    <Stack>
      {!objectId && (
        <>
          <Box my={2}>
            <EnlargedPhotoDialog
              photoUrl={pickedImage}
              onClose={() => {
                setPickedImage(O.None);
              }}
            />
            <Stack component="form" onSubmit={handleSubmit(handleSave)}>
              <Typography sx={{ fontSize: 18, paddingBottom: 2 }}>{t('areas.incomingFiber.label')}</Typography>
              <Stack gap={2} width="100%">
                <PhotosField
                  value={photosURLs}
                  onChange={setPhotosURLs}
                  onItemClick={(photoUrl) => {
                    setPickedImage(O.Some(photoUrl));
                  }}
                  entityType={entityType}
                  entityId={preGeneratedId}
                  propertyId={propertyId}
                />
                <TextField label="Name" {...register('text')} />
                <TextField label="How to find object" {...register('location')} />
                <TextField label={t('areas.fields.room')} {...register('room')} />
                <TextField
                  label={t('areas.fields.length')}
                  type="number"
                  inputProps={{ step: '0.1' }}
                  {...register('length', { valueAsNumber: true })}
                  helperText={getErrorMessage('length')}
                />
                <TextField
                  label={t('areas.fields.connectionCount')}
                  type="number"
                  {...register('connectionsCount', {
                    valueAsNumber: true,
                  })}
                  helperText={getErrorMessage('connectionsCount')}
                />
                <TextField label={t('areas.fields.netOwner')} {...register('netOwner')} />
              </Stack>
              <Grid container justifyContent="space-between" mt={2}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={!isValid || isLoadingCreateIncomingFiber}
                >
                  {isLoadingCreateIncomingFiber ? <CircularProgress size={16} /> : t('general.create')}
                </Button>
              </Grid>
            </Stack>
          </Box>
          <Box mt={2}>
            <Typography sx={{ fontSize: 18 }}>{t('areas.object.edit')}</Typography>
          </Box>
        </>
      )}
      {incomingFibers.length === 0 ? (
        <Alert severity="error" sx={{ width: 'unset' }}>
          {t('areas.object.noAvailable')}
        </Alert>
      ) : (
        <>
          <OptionalSelect
            options={incomingFibers.map(({ id, text }) => ({ id, label: O.getWithDefault(text, id) }))}
            value={objectId}
            onChange={setObjectId}
          />
          <Box mt={2}>
            {O.match(
              selectedObjectDetails,
              (details) => {
                if (details.type !== 'IncomingFiber') {
                  return null;
                }
                return <IncomingFiber key={details.id} details={details} onSave={onSave} />;
              },
              () => (
                <Alert severity="info" sx={{ width: 'unset' }}>
                  {t('areas.object.noSelected')}
                </Alert>
              ),
            )}
          </Box>
        </>
      )}
    </Stack>
  );
};
